import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useAttendance } from '../context/AttendanceContext';

const {
  FiUsers,
  FiCalendar,
  FiHome,
  FiTrendingUp,
  FiClock,
  FiTarget
} = FiIcons;

const Dashboard = ({ onNavigate }) => {
  const {
    members = [], // Provide default empty array
    currentQuarter,
    weeklyAttendance = [], // Provide default empty array
    getActiveMembers,
    isMeetingTime,
    getTodaysMeeting,
    isTuesday,
    isTodayMeetingCancelled,
    cancelTodaysMeeting,
    uncancelTodaysMeeting,
    loading,
    error,
    loadAllData,
    getAttendanceStats
  } = useAttendance();

  // Guard against undefined values 
  const activeMembers = getActiveMembers() || [];
  const totalMembers = members?.length || 0;
  const completedMeetings = weeklyAttendance?.filter(m => !m.isActive)?.length || 0;
  const todaysMeeting = getTodaysMeeting();
  
  // Get members eligible for prize (100% attendance)
  const attendanceStats = getAttendanceStats();
  const eligibleMembers = attendanceStats.filter(
    member => member.active && member.attendanceRate === 100
  );

  return (
    <div className="space-y-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="text-center"
      >
        <h2 className="text-4xl font-bold text-gray-800 mb-2">
          Welcome to MOCO PNG
        </h2>
        <p className="text-xl text-gray-600">
          Managing attendance for {currentQuarter}
        </p>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {[
          {
            title: 'Total Members',
            value: totalMembers,
            icon: FiUsers,
            color: 'bg-purple-500'
          },
          {
            title: 'Members Eligible for Prize',
            value: eligibleMembers.length,
            icon: FiTarget,
            color: 'bg-green-500'
          },
          {
            title: 'Total Meetings',
            value: completedMeetings,
            icon: FiCalendar,
            color: 'bg-blue-500'
          },
          {
            title: 'Current Quarter',
            value: currentQuarter,
            icon: FiTrendingUp,
            color: 'bg-indigo-500'
          }
        ].map((stat, index) => (
          <motion.div
            key={stat.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
            className="card"
          >
            <div className="card-body text-center">
              <div className={`w-16 h-16 ${stat.color} rounded-full flex items-center justify-center mx-auto mb-4`}>
                <SafeIcon icon={stat.icon} className="text-white text-2xl" />
              </div>
              <h3 className="text-lg font-semibold text-gray-700 mb-2">{stat.title}</h3>
              <p className="text-3xl font-bold text-gray-900">{stat.value}</p>
            </div>
          </motion.div>
        ))}
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
        className="card"
      >
        <div className="card-header">
          <h3 className="text-xl font-semibold flex items-center">
            <SafeIcon icon={FiCalendar} className="mr-3" />
            Today's Meeting Status
          </h3>
        </div>
        <div className="card-body">
          {isTuesday() ? (
            <div>
              <div className={`p-4 rounded-lg mb-4 ${
                isTodayMeetingCancelled() 
                  ? 'bg-red-50 border border-red-200' 
                  : (todaysMeeting?.isActive 
                    ? 'bg-green-50 border border-green-200' 
                    : 'bg-blue-50 border border-blue-200')
              }`}>
                <h4 className="font-semibold text-lg mb-2">
                  {isTodayMeetingCancelled() 
                    ? 'Meeting Cancelled' 
                    : (todaysMeeting?.isActive 
                      ? 'Meeting Active' 
                      : 'Meeting Scheduled')}
                </h4>
                <p>
                  {isTodayMeetingCancelled() 
                    ? "Today's meeting has been cancelled." 
                    : (todaysMeeting?.isActive 
                      ? `Currently ${todaysMeeting?.attendees?.length || 0} members have checked in.` 
                      : "Today's meeting is scheduled for 8:00 AM.")}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <h4 className="font-semibold text-gray-800 mb-2">Meeting Information</h4>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">Date:</span>
                      <span className="font-semibold text-gray-900">
                        {new Date().toLocaleDateString('en-US', {
                          weekday: 'long',
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">Time:</span>
                      <span className="font-semibold text-gray-900">8:00 AM - 9:00 AM</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">Location:</span>
                      <span className="font-semibold text-gray-900">Montgomery County Chamber</span>
                    </div>
                  </div>
                </div>

                <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                  <h4 className="font-semibold text-blue-800 mb-2">Quick Actions</h4>
                  <div className="space-y-3">
                    <button
                      onClick={() => onNavigate('attendance')}
                      className="w-full py-2 px-4 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center justify-center"
                    >
                      <SafeIcon icon={FiCalendar} className="mr-2" />
                      Manage Today's Meeting
                    </button>
                    <button
                      onClick={() => onNavigate('qr-generator')}
                      className="w-full py-2 px-4 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors flex items-center justify-center"
                    >
                      <SafeIcon icon={FiTarget} className="mr-2" />
                      Generate Check-in QR
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="p-6 text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={FiClock} className="text-blue-600 text-2xl" />
              </div>
              <h4 className="text-xl font-semibold text-gray-800 mb-2">No Meeting Today</h4>
              <p className="text-gray-600 mb-4">Meetings are held every Tuesday at 8:00 AM.</p>
              <div className="inline-flex">
                <button
                  onClick={() => onNavigate('history')}
                  className="py-2 px-4 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center"
                >
                  <SafeIcon icon={FiCalendar} className="mr-2" />
                  View Meeting History
                </button>
              </div>
            </div>
          )}
        </div>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="card"
        >
          <div className="card-header">
            <h3 className="text-xl font-semibold flex items-center">
              <SafeIcon icon={FiUsers} className="mr-3" />
              Membership Overview
            </h3>
          </div>
          <div className="card-body">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-gray-700">Active Members</span>
                <span className="font-semibold text-green-600">{activeMembers.length}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-green-500 h-2 rounded-full"
                  style={{ width: `${(activeMembers.length / Math.max(totalMembers, 1)) * 100}%` }}
                ></div>
              </div>

              <div className="flex items-center justify-between mt-4">
                <span className="text-gray-700">Members Needing Outreach</span>
                <span className="font-semibold text-yellow-600">
                  {members.filter(m => m.active && m.consecutiveAbsences && m.consecutiveAbsences > 0).length}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-yellow-500 h-2 rounded-full"
                  style={{ width: `${(members.filter(m => m.active && m.consecutiveAbsences && m.consecutiveAbsences > 0).length / Math.max(activeMembers.length, 1)) * 100}%` }}
                ></div>
              </div>

              <div className="mt-4 text-center">
                <button
                  onClick={() => onNavigate('members')}
                  className="py-2 px-4 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center mx-auto"
                >
                  <SafeIcon icon={FiUsers} className="mr-2" />
                  Manage Members
                </button>
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="card"
        >
          <div className="card-header">
            <h3 className="text-xl font-semibold flex items-center">
              <SafeIcon icon={FiTrendingUp} className="mr-3" />
              Attendance Trends
            </h3>
          </div>
          <div className="card-body">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-gray-700">Meetings This Quarter</span>
                <span className="font-semibold text-blue-600">{completedMeetings}</span>
              </div>

              <div className="flex items-center justify-between mt-4">
                <span className="text-gray-700">Average Attendance</span>
                <span className="font-semibold text-purple-600">
                  {completedMeetings > 0
                    ? Math.round(
                        weeklyAttendance
                          .filter(m => !m.isActive)
                          .reduce((sum, meeting) => sum + (meeting.attendees?.length || 0), 0) / completedMeetings
                      )
                    : 0} members
                </span>
              </div>

              <div className="mt-4 text-center">
                <button
                  onClick={() => onNavigate('results')}
                  className="py-2 px-4 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors flex items-center mx-auto"
                >
                  <SafeIcon icon={FiTrendingUp} className="mr-2" />
                  View Quarterly Results
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Dashboard;